import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, User, Mail, Phone, MessageSquare, Briefcase, DollarSign } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { submitBooking, getServices } from '../data/api';
import { useToast } from '../hooks/use-toast';

const BookingModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', service: '',
    preferredDate: '', preferredTime: '', projectType: '', budget: '', message: ''
  });

  useEffect(() => {
    if (isOpen) getServices().then(setServices).catch(() => {});
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSelect = (field, value) => setForm({ ...form, [field]: value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await submitBooking(form);
      if (result.status === 'ok') {
        toast({ title: "Consultation Booked!", description: "We'll contact you to confirm your appointment." });
        setForm({ name: '', email: '', phone: '', service: '', preferredDate: '', preferredTime: '', projectType: '', budget: '', message: '' });
        onClose();
      }
    } catch {
      toast({ title: "Error", description: "Failed to book. Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-700 to-blue-600 px-6 py-5 rounded-t-2xl flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Book a Consultation</h2>
            <p className="text-blue-200 text-sm mt-1">Free consultation for your project</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors" data-testid="booking-modal-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                <User className="w-3.5 h-3.5" /> Full Name
              </Label>
              <Input name="name" value={form.name} onChange={handleChange} required placeholder="Your name" data-testid="booking-name" />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                <Phone className="w-3.5 h-3.5" /> Phone
              </Label>
              <Input name="phone" value={form.phone} onChange={handleChange} required placeholder="+65 XXXX XXXX" data-testid="booking-phone" />
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
              <Mail className="w-3.5 h-3.5" /> Email
            </Label>
            <Input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="your@email.com" data-testid="booking-email" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                <Briefcase className="w-3.5 h-3.5" /> Service
              </Label>
              <Select onValueChange={(v) => handleSelect('service', v)} value={form.service}>
                <SelectTrigger data-testid="booking-service">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                <Briefcase className="w-3.5 h-3.5" /> Project Type
              </Label>
              <Select onValueChange={(v) => handleSelect('projectType', v)} value={form.projectType}>
                <SelectTrigger data-testid="booking-project-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Restaurant / Cafe">Restaurant / Cafe</SelectItem>
                  <SelectItem value="Retail Shop">Retail Shop</SelectItem>
                  <SelectItem value="Office">Office</SelectItem>
                  <SelectItem value="Residential">Residential</SelectItem>
                  <SelectItem value="Industrial">Industrial</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                <Calendar className="w-3.5 h-3.5" /> Preferred Date
              </Label>
              <Input name="preferredDate" type="date" value={form.preferredDate} onChange={handleChange} data-testid="booking-date" />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
                <Clock className="w-3.5 h-3.5" /> Preferred Time
              </Label>
              <Select onValueChange={(v) => handleSelect('preferredTime', v)} value={form.preferredTime}>
                <SelectTrigger data-testid="booking-time">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="9:00 AM">9:00 AM</SelectItem>
                  <SelectItem value="10:00 AM">10:00 AM</SelectItem>
                  <SelectItem value="11:00 AM">11:00 AM</SelectItem>
                  <SelectItem value="2:00 PM">2:00 PM</SelectItem>
                  <SelectItem value="3:00 PM">3:00 PM</SelectItem>
                  <SelectItem value="4:00 PM">4:00 PM</SelectItem>
                  <SelectItem value="5:00 PM">5:00 PM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
              <DollarSign className="w-3.5 h-3.5" /> Budget Range
            </Label>
            <Select onValueChange={(v) => handleSelect('budget', v)} value={form.budget}>
              <SelectTrigger data-testid="booking-budget">
                <SelectValue placeholder="Select budget range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Below $10,000">Below $10,000</SelectItem>
                <SelectItem value="$10,000 - $30,000">$10,000 - $30,000</SelectItem>
                <SelectItem value="$30,000 - $50,000">$30,000 - $50,000</SelectItem>
                <SelectItem value="$50,000 - $100,000">$50,000 - $100,000</SelectItem>
                <SelectItem value="Above $100,000">Above $100,000</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-1.5">
              <MessageSquare className="w-3.5 h-3.5" /> Project Details
            </Label>
            <Textarea name="message" value={form.message} onChange={handleChange} rows={3} placeholder="Tell us about your project..." data-testid="booking-message" />
          </div>

          <Button type="submit" disabled={submitting} data-testid="booking-submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-base font-semibold">
            {submitting ? 'Booking...' : 'Book Free Consultation'}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            We'll confirm your appointment within 24 hours
          </p>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;
